import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';

/**
 * Project A's own funds, and the one way a code that is not one yet becomes
 * one. A holding is refused for a code the table does not have, so every form
 * that takes a free-typed code asks first and creates it through here.
 */
export function useProjectAFunds(): {
  codes: string[];
  isKnown: (code: string) => boolean;
  create: (code: string) => Promise<boolean>;
} {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const funds = useQuery(trpc.projectAFunds.list.queryOptions());
  const upsert = useMutation(trpc.projectAFunds.upsert.mutationOptions());

  const codes = (funds.data ?? []).map((fund) => fund.code);
  return {
    codes,
    isKnown: (code) => codes.includes(code),
    create: async (code) => {
      try {
        await upsert.mutateAsync({ code });
      } catch (error) {
        toast(error instanceof Error ? error.message : 'Could not add that fund.');
        return false;
      }
      await queryClient.invalidateQueries({ queryKey: trpc.projectAFunds.list.queryKey() });
      await queryClient.invalidateQueries({ queryKey: trpc.coInvestments.options.queryKey() });
      toast(`${code} is now a Project A fund.`, { tone: 'info' });
      return true;
    },
  };
}
